import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import instanceAxios from '@/util/axios';
import { feedBannersUrl } from '@/constants';

export type FeedBanner = {
  id: number;
  name: string;
  fileUrl: string;
  fileType: 'image' | 'video';
  isActive: boolean;
  link: string | null;
  address: string | null;
};

const ROTATE_MS = 3000;

const shuffleBanners = (items: FeedBanner[]) => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const resolveLink = (link: string) => (/^https?:\/\//i.test(link) ? link : `https://${link}`);

export const FeedBannersCarousel = ({ refreshKey = 0 }: { refreshKey?: number }) => {
  const [shuffled, setShuffled] = useState<FeedBanner[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    instanceAxios
      .get<FeedBanner[]>(feedBannersUrl)
      .then(response => {
        setShuffled(shuffleBanners(response.data || []));
        setCurrentIndex(0);
      })
      .catch(() => setShuffled([]));
  }, [refreshKey]);

  useEffect(() => {
    if (shuffled.length <= 1) {
      return;
    }
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % shuffled.length);
    }, ROTATE_MS);
    return () => clearInterval(timer);
  }, [shuffled.length]);

  if (shuffled.length === 0) {
    return null;
  }

  const current = shuffled[currentIndex];
  const href = current.link?.trim() ? resolveLink(current.link.trim()) : undefined;
  const mediaSx = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    pointerEvents: 'none',
    WebkitUserDrag: 'none'
  } as const;

  return (
    <Box sx={{ width: '100%', my: 2, px: { xs: 0, sm: 1 } }}>
      <Box
        component={href ? 'a' : 'div'}
        href={href}
        target={href ? '_blank' : undefined}
        rel={href ? 'noopener noreferrer' : undefined}
        sx={{
          display: 'block',
          width: '100%',
          overflow: 'hidden',
          borderRadius: { xs: 0, sm: 1 },
          cursor: href ? 'pointer' : 'default',
          aspectRatio: '16 / 6',
          bgcolor: 'background.default',
          color: 'inherit',
          textDecoration: 'none',
          WebkitTapHighlightColor: 'transparent'
        }}
      >
        {current.fileType === 'video' ? (
          <Box key={current.id} component="video" src={current.fileUrl} autoPlay muted loop playsInline sx={mediaSx} />
        ) : (
          <Box key={current.id} component="img" src={current.fileUrl} alt="" draggable={false} sx={mediaSx} />
        )}
      </Box>

      {shuffled.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1.5 }}>
          {shuffled.map((banner, index) => (
            <Box
              key={banner.id}
              onClick={() => setCurrentIndex(index)}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: index === currentIndex ? 'primary.main' : 'action.disabled',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
